import { useContext } from 'react';
import { createCachedSelector } from 're-reselect';
import { useQuery } from 'react-query';
import { useRecoilValue } from 'recoil';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import * as sharedAtoms from '../../atoms/shared';
import * as api from '../../services/api';
import useCRUD from '../useCRUD';
import { API_TIME_FORMAT, QUERY_CACHE_TIME } from '../../constants';
import { SORT_BY_TIME } from '../../utils/sorting';
import useAccount from './useAccount';
import useBranches from './useBranches';

const SORT_BY_LABEL_PRIORITY = (a, b) => {
  const sortByPriorityResult = (a?.priority || 0) - (b?.priority || 0);

  if (sortByPriorityResult === 0) {
    return SORT_BY_TIME('startTime', API_TIME_FORMAT)(a, b);
  }

  return sortByPriorityResult;
};

const selector = createCachedSelector(
  (data) => data.data,
  (data, theme) => theme,
  (data, theme, locationId) => locationId,
  (data, theme, locationId, t) => t,
  (data, theme, locationId, t, branchId) => branchId,
  (data, theme, locationId, t, branchId, filterByBranch) => filterByBranch,

  (data = [], theme, locationId, t, branchId, filterByBranch) => {
    const lastIndexByBranch = new Map();

    const labels = [];
    const labelOptions = [];
    const mappedLabels = {
      null: {
        title: t('events'),
        color: theme.colors.blue,
        background: theme.colors.hoverBackground
      }
    };
    // reset color index for each branch to make the color constants
    // eslint-disable-next-line no-restricted-syntax
    for (const label of data) {
      const newIndex = (lastIndexByBranch.get(label.branches[0]) ?? -1) + 1;
      lastIndexByBranch.set(label.branches[0], newIndex);
      const formattedLabel = {
        id: label.id,
        title: label.title,
        locationId: label.locationId || locationId,
        startTime: label.startTime,
        endTime: label.endTime,
        isDeleted: label.isDeleted,
        branches: label.branches,
        color: theme.colors.palette[newIndex % theme.colors.palette.length].dark,
        background: theme.colors.palette[newIndex % theme.colors.palette.length].light,
        index: newIndex,
        priority: label.priority
      };
      mappedLabels[label.id] = formattedLabel;

      const labelOption = {
        value: formattedLabel.id,
        label: formattedLabel.title,
        ...formattedLabel
      };

      if (filterByBranch) {
        if (label.branches.includes(branchId)) {
          labels.push(formattedLabel);
          if (!label.isDeleted) {
            labelOptions.push(labelOption);
          }
        }
      } else {
        labels.push(formattedLabel);
        if (!label.isDeleted) {
          labelOptions.push(labelOption);
        }
      }
    }

    const sortedLabels = labels.sort(SORT_BY_LABEL_PRIORITY);
    const sortedLabelOptions = labelOptions.sort(SORT_BY_LABEL_PRIORITY);

    return { labels: sortedLabels, labelOptions: sortedLabelOptions, mappedLabels };
  }
)({
  keySelector: (data, theme, locationId, t, branchId, filterByBranch, storeKey) =>
    `${storeKey.join('#')}#${locationId}#${branchId}#${filterByBranch}`
});

const useLabels = ({ filterByBranch = true, branchId: customBranchId } = {}) => {
  const { accountId } = useAccount();
  const defaultBranchId = useRecoilValue(sharedAtoms.selectedBranch);
  const branchId = customBranchId ? parseInt(customBranchId, 10) : defaultBranchId;
  const { t, theme } = useContext(ConfigContext);
  const { mappedBranches } = useBranches();

  const storeKey = ['labels', accountId];
  const locationId = mappedBranches?.[branchId]?.locationId;

  const { isLoading, data } = useQuery(storeKey, () => api.fetchLabels({ accountId }), {
    enabled: !!accountId,
    cacheTime: QUERY_CACHE_TIME,
    select: (labelsData) => selector(labelsData, theme, locationId, t, branchId, filterByBranch, storeKey)
  });

  const { labels = [], labelOptions = [], mappedLabels = {} } = data || {};

  const { addItem, editItem, deleteItem } = useCRUD(
    storeKey,
    {
      addApi: (label) => api.addLabel({ accountId, branchId, ...label }),
      editApi: ({ id, ...label }) => api.updateLabel({ accountId, branchId, labelId: id, ...label }),
      deleteApi: ({ id }) => api.deleteLabel({ accountId, branchId, labelId: id })
    },
    { formatItem: (item) => ({ ...item, branches: [branchId] }) }
  );

  const { editItem: updateLabels } = useCRUD(storeKey, {
    editApi: (labelsData) => api.updateLabels({ accountId, branchId, labels: labelsData })
  });

  return {
    isLoading,
    labels,
    labelOptions,
    mappedLabels,
    updateLabels,
    addLabel: addItem,
    editLabel: editItem,
    deleteLabel: deleteItem
  };
};

export default useLabels;
